import { ChannelPreview } from "@/components/conversations/channel-preview.tsx";
import SBChannelList from "@sendbird/uikit-react/ChannelList";
import { GroupChannelListQueryParamsInternal } from "@sendbird/uikit-react/ChannelList/context";
import { useMemo, useState } from "react";
import { ChannelListHeader } from "../conversations/channel-list-header";
import { UnreadChannelFilter } from "@sendbird/chat/groupChannel";
import { shouldShowCheckinBadge } from "@/lib/utils/check-in-badge";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";

interface ChannelListingProps {
  currentChannelUrl: string | null;
  setCurrentChannelUrl: (url: string) => void;
  customTypesFilter?: string[];
  disableAutoSelect?: boolean;
  showRoleBadge?: boolean;
  shouldEverShowCheckinBadge?: boolean;
}

export default function ChannelListing({
  currentChannelUrl,
  setCurrentChannelUrl,
  customTypesFilter = [],
  disableAutoSelect = false,
  showRoleBadge = false,
  shouldEverShowCheckinBadge = false,
}: ChannelListingProps) {
  const store = useSendbirdStateContext();
  const [searchText, setSearchText] = useState<string>("");
  const [selectedFilter, setSelectedFilter] = useState<
    "all" | "unread" | "check-in"
  >("all");

  const channelListQueries = useMemo(
    () =>
      ({
        channelNameContainsFilter: searchText,
        nicknameContainsFilter: searchText,
        order: "latest_last_message" as any,
        includeFrozen: false,
        includeEmpty: true,
        customTypesFilter: customTypesFilter,
        unreadChannelFilter:
          selectedFilter === "unread"
            ? UnreadChannelFilter.UNREAD_MESSAGE
            : UnreadChannelFilter.ALL,
      }) satisfies GroupChannelListQueryParamsInternal,
    [searchText, customTypesFilter, selectedFilter],
  );

  const queries = useMemo(
    () => ({
      channelListQuery: channelListQueries,
    }),
    [channelListQueries],
  );

  return (
    <SBChannelList
      className="max-w-48 border-r border-zinc-300 sm:max-w-[200px] lg:max-w-[400px]"
      onChannelSelect={(channel) => {
        if (channel && channel.url) setCurrentChannelUrl(channel.url);
      }}
      disableAutoSelect={disableAutoSelect}
      renderHeader={() => {
        return (
          <ChannelListHeader
            search={searchText}
            setSearch={setSearchText}
            selectedFilter={selectedFilter}
            shouldEverShowCheckinBadge={shouldEverShowCheckinBadge}
            setSelectedFilter={setSelectedFilter}
          />
        );
      }}
      queries={queries}
      renderChannelPreview={({ channel }) => {
        // Filter channels based on selected filter
        if (selectedFilter === "check-in") {
          const shouldShow = shouldShowCheckinBadge(
            channel,
            store.config.userId,
          );
          if (!shouldShow) {
            return <div className="hidden" />;
          }
        }

        return (
          <ChannelPreview
            channel={channel}
            currentChannelUrl={currentChannelUrl}
            showRoleBadge={showRoleBadge}
            shouldEverShowCheckinBadge={shouldEverShowCheckinBadge}
          />
        );
      }}
    />
  );
}
